import { generatePath } from '@toggle/helpers';
import {
  conversations,
  SearchConversationResponse,
  WebSearchSettings,
} from '@toggle/toggle';

import { apiFetch, wretchRequest } from '~/utils/api-fetch/apiFetch';

import { Conversation, ConversationTask } from '../use-chat/use-chat.types';

export const FETCH_ALL_CONVERSATIONS_PAGE_SIZE = 50;

export type SubmittedAnalysisMode = 'Deep' | 'Auto';
export type WebSearch = 'restricted' | 'open';

type BasePostConversationArgs = {
  message: string;
  analysis_mode?: SubmittedAnalysisMode;
  tools_config?: {
    parameters: {
      web_search?: {
        sources: string[];
      };
    };
  };
  attachments?: string[];
  controller?: AbortController;
};
type PostContinueConversationArgs = BasePostConversationArgs & {
  conversationId: string;
};

export const postConversation = async ({
  message,
  analysis_mode,
  tools_config,
  attachments,
  controller,
}: BasePostConversationArgs) => {
  return apiFetch(conversations.postConversation.path, {
    schema: conversations.postConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const postContinueConversation = async ({
  message,
  conversationId,
  analysis_mode,
  controller,
  tools_config,
  attachments,
}: PostContinueConversationArgs) => {
  return apiFetch(conversations.continueConversation.path(conversationId), {
    schema: conversations.continueConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const getConversationTasks = async (
  id: string,
  signal?: AbortSignal
) => {
  let page = 1;
  const allTasks: ConversationTask[] = [];
  let conversationData: Conversation | undefined = undefined;
  let hasMorePages = true;

  while (hasMorePages) {
    const requestPath = generatePath({
      basePath: conversations.getConversationTask.path(id),
      params: {
        page,
      },
    });

    const result = await apiFetch(requestPath, {
      schema: conversations.getConversationTask.schema,
      signal,
    });

    if (result.error) {
      return result;
    }

    if (page === 1 && result.data) {
      conversationData = {
        ...result.data,
        public_copies: [],
      };
    }

    if (result.data?.tasks && result.data.tasks.length > 0) {
      allTasks.push(...result.data.tasks);
      page++;
    } else {
      hasMorePages = false;
    }
  }

  return {
    data: conversationData
      ? { ...conversationData, tasks: allTasks }
      : undefined,
  };
};

export const searchConversation = (query: string, page: number) => {
  const requestPath = generatePath({
    basePath: conversations.searchConversation.path,
    params: {
      q: query,
      page,
    },
  });

  return wretchRequest<SearchConversationResponse>(requestPath, {
    schema: conversations.searchConversation.schema,
  });
};

type GetConversationTaskStatesArgs = {
  conversationId: string;
  taskRequestId: string;
};

export const getConversationTaskStates = ({
  conversationId,
  taskRequestId,
}: GetConversationTaskStatesArgs) => {
  return apiFetch(
    conversations.getConversationMessage.path(conversationId, taskRequestId),
    {
      schema: conversations.getConversationMessage.schema,
    }
  );
};

export const getConversationMessageWithSteps = ({
  messageId,
  conversationId,
  controller,
}: {
  messageId: string;
  conversationId: string;
  controller?: AbortController;
}) => {
  return wretchRequest(
    conversations.getConversationMessage.path(conversationId, messageId),
    {
      schema: conversations.getConversationMessage.schema,
      signal: controller?.signal,
    }
  );
};

export const updateConversation = ({
  id,
  name,
  favourite,
}: {
  id: string;
  name?: string;
  favourite?: boolean;
}) => {
  const body = favourite !== undefined ? { name, favourite } : { name };
  return apiFetch(conversations.conversationId.path(id), {
    method: 'put',
    body,
  });
};

export const deleteConversation = (id: string) =>
  apiFetch(conversations.conversationId.path(id), {
    method: 'delete',
  });

export const getAllConversations = (page: number) => {
  const requestPath = generatePath({
    basePath: conversations.getAllConversations.path,
    params: {
      page,
      page_size: FETCH_ALL_CONVERSATIONS_PAGE_SIZE,
    },
  });

  return wretchRequest(requestPath, {
    schema: conversations.getAllConversations.schema,
  });
};

export const postFeedback = (body: {
  conversation_id: string;
  request_id: string;
  feedback: string;
}) => {
  wretchRequest(conversations.feedback.path, {
    method: 'post',
    body,
  });
};

export const abortRequest = (id: string) =>
  apiFetch(conversations.abortConversation.path(id), {
    method: 'put',
    body: { action: 'cancel' },
  });

export const copyConversation = (id: string, name: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'public',
      name,
    },
  });

export const postClonePublicConversation = (id: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'private',
    },
  });

export const getCustomSources = () =>
  apiFetch(conversations.getCustomSources.path, {
    schema: conversations.getCustomSources.schema,
  });

export const updateCustomSources = (sources: WebSearchSettings) =>
  apiFetch(conversations.updateCustomSources.path, {
    method: 'put',
    body: sources,
  });

export const resetCustomSources = () =>
  apiFetch(conversations.resetCustomSources.path, {
    method: 'delete',
  });
